import React, { useEffect, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
    Alert,
    Box,
    Grid,
    Stack,
    MenuItem,
    Menu,
    Checkbox,
    Tabs,
    Tab,
    Typography,
    Button,
    Tooltip
} from '@mui/material';
import NoPresentationsSIMP from '../../assets/image/empty-state-establecimientos-simp.png';
import NoPresentationsBO from '../../assets/image/empty-state-establecimientos-backoffice.png';
import ToggleStateIcon from '@mui/icons-material/ToggleOffOutlined';
import PencilIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from 'react-router-dom';
import { useRoles } from '../../hooks/useRoles';
import { useTranslation } from 'react-i18next';
import usePresentations from './hooks/usePresentations';
import DataTable from '../../components/DataTable/DataTable';
import styles from './styles.module.css';
import { useGetCancerigenosPdfMutation } from '../../services/presentacionesApi';
import useGlobalFilters from '../../hooks/useGlobalFilters';
import { downloadBase64File, downloadFromBase64, getDate } from '../../utils/common';
import TableActionButton from './components/TableActionButton';
import {
    CARCINOGENIC_SRT_STATES,
    PresentationSRTStateChip,
    PresentationStateChip
} from './components/PresentationStateChip';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import {
    ALERT_CONFIRM_ACTIONS,
    ALERT_TITLES,
    ALERT_TYPE,
    ALERT_TYPES,
    isContratoInactivo,
    PRESENTATION_STATE
} from './consts';
import BackdropComponent from '../../components/Backdrop/BackdropComponent';
import { useUpdatePresentationStates } from './hooks/useUpdatePresentationStates';
import { AlertDialog } from '../../components/Alert/AlertDialog';
import useReports, { PdfType } from './hooks/useReports';
import { useAppDispatch } from '../../hooks/reduxHooks';
import { setPresentacionItemSelected } from '../../redux/slices/rarPresentacionSlice';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useUserFeatures from '../../hooks/useUserFeatures';
import SectionHeader from '../../components/SectionHeader';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import FiltersComponent from './components/FiltersComponent';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import useCancerigenos from './hooks/useCancerigenos';
import {
    clearSelectedReportData,
    setSelectedCarcinogenicReportId
} from '../../redux/slices/carcinogenicReportSlice';
import { showRgrlDialogAlert } from '../../features/slices/applicationSlice';
import { useDownloadRGRLFilesOptionsDialog } from '../../components/Dialogs/DownloadRGRLFilesOptionsDialog';
import { PRESENTATION_TABS_INDEX } from '../../redux/slices/presentationsSliceBO';

import { PresentationsBOMassiveSelectionStack } from './components/PresentationsBOMassiveSelectionStack';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DrawerManager, {
    PRESENTATIONS_DRAWER_ACTION_VALUES
} from './components/drawers/DrawerManager';
import { SRT_STATES } from '../GeneracionArchivosSRT/consts/states';
import PresentacionesSVCCIndex from '../PresentacionesSVCC/PresentacionesSVCCIndex';
import useContracts from '../../components/layout/ContractSelector/hook/useContracts';
import { useGetOrganismsQuery } from '../../services/organismsApi';
import { useGetEmpresasQuery } from '../../services/companyApi';
import { ESTILO_TABLA_PRESENTACIONES } from './estiloTablaPresentaciones';
import usePestanaDesdeUrl from './hooks/usePestanaDesdeUrl';
import useOrganizationSelector from '../../components/layout/OrganizationSelector/hook/useOrganizationSelector';

const ESTILO_HEADER_COMPACTO = {
    '& h1': { fontSize: '22px !important', marginTop: '14px', marginBottom: '4px' },
    '& .MuiGrid-direction-xs-column': { borderRadius: '12px 12px 0 0' },
    // Exportar Excel (SectionHeader.IconHeader) venía en 48 px: a la altura del header compacto.
    '& .MuiGrid-direction-xs-column .MuiButton-root': {
        height: '36px !important',
        minHeight: 0,
        minWidth: 'auto !important',
        borderRadius: '8px !important',
        fontSize: '13px !important',
        fontWeight: 600,
        paddingLeft: '16px !important',
        paddingRight: '16px !important',
        marginTop: '12px'
    }
};

/*
 * Pestañas como píldoras dentro de una barra blanca redondeada, igual que los
 * pasos del informe SVCC. Sólo esta pantalla.
 */
const TabsStyle = {
    minHeight: 0,
    width: 'max-content',
    bgcolor: '#FFFFFF',
    borderRadius: '12px',
    p: 0.75,
    mb: 1.5,
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTabs-flexContainer': { gap: 0.5 }
};

const TabStyle = {
    minHeight: 36,
    py: 0.75,
    px: 2.25,
    borderRadius: '999px',
    fontSize: 14,
    fontWeight: 600,
    color: '#3D4A50',
    '&.Mui-selected': { bgcolor: 'primary.main', color: '#FFFFFF' }
};

export const getPresentationSVCCStateSRT = (srtData: any) => {
    if (!srtData) return CARCINOGENIC_SRT_STATES.PENDING;

    const tieneError = Boolean(
        srtData?.log?.errorCode || srtData?.log?.statusCode || srtData?.log?.errorMessage
    );
    if (tieneError) return CARCINOGENIC_SRT_STATES.ERROR;

    // paso >= 4: los 4 pasos de vinculación (Inicio/AnexoI/AnexoV/Nómina) ya
    // se completaron con éxito, o incluso se llegó a Consolidación (paso 5)
    // — cualquiera de los dos es "presentado" desde la perspectiva del drawer.
    if (srtData?.paso >= 4 && srtData?.log?.success === true) {
        return CARCINOGENIC_SRT_STATES.PRESENTED;
    }

    return CARCINOGENIC_SRT_STATES.PENDING;
};

const tooltipPresentationDateContent = ({ presentation }: { presentation: any }) => {
    return (
        <Box>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                USUARIO
            </Typography>

            <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {`${presentation?.usuarioCreacion?.nombre ?? ''} ${presentation?.usuarioCreacion?.apellido ?? '-'}`}
            </Typography>

            <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    ROL
                </Typography>

                <Typography>{presentation?.usuarioCreacion?.roles[0]?.nombre ?? '-'}</Typography>
            </Box>
        </Box>
    );
};

const columnTitles = ({ t, currentTabIndex, hideSrtState }: any): string[] => {
    const columns: string[] = [];

    columns.push(t('table.columns.id'));

    // Add specific columns for RGRL and RAR tabs
    if (currentTabIndex === PRESENTATION_TABS_INDEX.RGRL) {
        columns.push(t('table.columns.isCarcinogenic'));
    }
    if (currentTabIndex === PRESENTATION_TABS_INDEX.RAR) {
        columns.push(t('table.columns.isExposed'));
    }

    // Common columns
    columns.push(
        t('table.columns.contrato'),
        t('table.columns.establecimiento'),
        t('table.columns.presentationDate'),
        t('table.columns.state')
    );

    if (currentTabIndex == PRESENTATION_TABS_INDEX.SVCC && !hideSrtState) {
        columns.push(t('table.columns.stateSRT'));
    }

    columns.push(t('table.columns.actions'));

    return columns;
};

const columnWidths = (currentTabIndex: number, hideSrtState?: boolean) =>
    currentTabIndex !== PRESENTATION_TABS_INDEX.SVCC
        ? ['3%', '3%', '3%', '35%', '35%', '10%', '10%', '4%']
        : hideSrtState
          ? ['3%', '40%', '20%', '15%', '8%', '2%']
          : ['3%', '40%', '20%', '15%', '8%', '8%', '2%'];

const PresentacionesModuleIndex: React.FC = () => {
    const { FEATURES } = useUserFeatures();
    const dispatch = useAppDispatch();
    usePestanaDesdeUrl();
    const [isShowMoreFiltersOpen, setIsShowMoreFiltersOpen] = useState<boolean>(false);
    const [processPdfDownload, setProccessPdfDownload] = useState(false);
    const { isAdminBO, isSupervisorSIMP, isOperatorSIMP, isViewerBO, isProvider } = useRoles();
    const { isMobile, isTablet } = useIsMobile();
    const { t } = useTranslation('presentaciones');
    const navigate = useNavigate();
    const [adminSelectedItem, setAdminSelectedItem] = useState<any>(null);
    const [downloadingPDF, setDownloadingPDF] = useState<boolean>(false);
    const [triggerGetCancerigenosPdf] = useGetCancerigenosPdfMutation();
    /**
     * El filtro "Contrato" de la barra guarda el id del organismo central, pero
     * la grilla SVCC trabaja por CUIT: se resuelve acá el CUIT de ese contrato
     * y se le pasa a la grilla. `rawContracts` ya viene cacheado por RTK Query,
     * es la misma consulta que usa el propio filtro.
     */
    const { rawContracts } = useContracts();

    const {
        filters,
        setFilters,
        extraFilters,
        handleSubmitSearch,
        handleChangeExtraFilters,
        clearFilters
    } = useGlobalFilters();

    /**
     * Cliente/Contrato/Organismo/Establecimiento ahora traen su propio estado
     * `activo` (y el establecimiento, además, el de su organismo/contrato/
     * cliente anidados). Estas consultas usan los mismos parámetros que
     * `FiltersComponent`, así que RTK Query comparte la cache en vez de
     * duplicar el pedido.
     */
    const { organizations: rawOrganizaciones } = useOrganizationSelector();
    const { data: { data: { data: rawOrganismos } } = { data: { data: [] } } } =
        useGetOrganismsQuery({
            idOrganizacion: extraFilters?.idOrganizacion || undefined,
            skip: 0,
            take: 99999
        });
    const { data: rawEstablecimientosResponse } = useGetEmpresasQuery({
        skip: 0,
        take: 5000,
        idOrganismoCentral: extraFilters?.idOrganismoCentral
    });
    const rawEstablecimientos = rawEstablecimientosResponse?.data?.data ?? [];

    // Se resuelve el estado "activo" del filtro más específico seleccionado
    // primero: si hay establecimiento, sus campos anidados (organismo,
    // organismoCentral, organizacion) ya cuentan todo — no hace falta
    // volver a mirar los filtros idOrganismo/idOrganismoCentral/idOrganizacion
    // por separado (evita repetir el mismo aviso dos veces).
    const establecimientoSeleccionado = extraFilters?.idEstablecimiento
        ? rawEstablecimientos.find((e: any) => e.id === extraFilters.idEstablecimiento)
        : undefined;

    const organismoSeleccionado = establecimientoSeleccionado
        ? establecimientoSeleccionado.organismo
        : extraFilters?.idOrganismo
          ? rawOrganismos.find((o: any) => o.id === extraFilters.idOrganismo)
          : undefined;

    const establecimientoActivo = establecimientoSeleccionado?.activo;
    const organismoActivo = organismoSeleccionado?.activo;
    const contratoActivo = establecimientoSeleccionado
        ? establecimientoSeleccionado.organismo?.organismoCentral?.activo
        : extraFilters?.idOrganismo
          ? organismoSeleccionado?.contratoActivo
          : extraFilters?.idOrganismoCentral
            ? rawContracts.find((c: any) => c.id === extraFilters.idOrganismoCentral)?.activo
            : undefined;
    const clienteActivo = establecimientoSeleccionado
        ? establecimientoSeleccionado.organizacion?.activo
        : extraFilters?.idOrganizacion
          ? rawOrganizaciones.find((o: any) => o.id === extraFilters.idOrganizacion)?.activo
          : undefined;

    const {
        selected,
        openCard,
        menuOpen,
        menuOpenAdmin,
        edittingPresentationState,
        establishmentDetail,
        anchorEl,
        isLoadingRARsFile,
        setAnchorEl,
        handleClickAction,
        handleClickActionAdmin,
        setSelected,
        handleCloseMenu,
        handleCloseMenuAdmin,
        refetchPresentations,
        handleDownloadRARsFile,
        presentationsSelection,
        presentationsTabs,
        handleMassiveStateChangeClick,
        handleMassiveExportClick,
        isUpdatingMassivePresentationsStates,
        isLoadingMassiveExportByIds,
        presentationsDrawerManager,
        handleSendCarcinogenicReportToSRT,
        isSendingCarcinogenicReportToSRT,
        handleExportRGRLPresentaciones,
        isExportingRGRL
    } = usePresentations({ filters, extraFilters, clearFilters });

    const { currentTabIndex, result, handleTabChange, isFetching } = presentationsTabs;

    const { handleApproveCancerigeno } = useCancerigenos({ refetchPresentations });
    const { handleRGRLDownload, isFetchingPDFLink } = useDownloadRGRLFilesOptionsDialog();

    const {
        currentSelectedItems: selectedPresentations,
        isSelected,
        handleSelectClick,
        handleSelectAllClick,
        areAllItemsSelected,
        AT_LEAST_ONE_ITEM_SELECTED: AT_LEAST_ONE_PRESENTATION_SELECTED
    } = presentationsSelection;

    // Las presentaciones en estado Inactivo (idEstado 10, ídem contrato dado
    // de baja) quedan de solo lectura: no se pueden sumar a una acción
    // masiva de cambio de estado, así que no deben poder ni seleccionarse.
    const isPresentationSelectable = (item: any): boolean =>
        item?.idEstado !== PRESENTATION_STATE.INACTIVO && !isContratoInactivo(item);

    const selectablePresentations = (items: any[]): any[] => items.filter(isPresentationSelectable);

    const itemPresentationData = (data: any): { data: { data: any[]; count: number } } => {
        if (Array.isArray(data?.data)) {
            return { data: { data: data.data, count: data.count } };
        }
        return {
            data: {
                data: [],
                count: 0
            }
        };
    };

    const itemTable =
        result && 'data' in result
            ? itemPresentationData(result.data)
            : itemPresentationData(undefined);

    // `selected` es un snapshot de useState tomado al clickear la fila — no
    // se resincroniza solo cuando la cache de RTK Query se invalida (ej. tras
    // vincular/consolidar/cancelar SRT). Sin esto, el drawer ya abierto puede
    // seguir mostrando el estado SRT viejo hasta que se cierre y reabra.
    useEffect(() => {
        if (!selected?.id) return;
        const freshRow = itemTable.data.data.find((row: any) => row.id === selected.id);
        if (freshRow && freshRow !== selected) {
            setSelected(freshRow);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemTable.data.data]);

    const {
        showConfirmActionAlert,
        alertType,
        updatingPresentationState,
        updatePresentationStateHandler,
        setShowConfirmActionAlert,
        setAlertType
    } = useUpdatePresentationStates();

    const { handleDownloadPdf, isLoading } = useReports(
        selected?.id,
        establishmentDetail?.nombre,
        establishmentDetail?.codigoSRT
    );

    const handleDownloadPresentation = async (item: any) => {
        try {
            setDownloadingPDF(true);
            handleCloseMenu();
            handleCloseMenuAdmin();

            if (!item?.id) return;
            const pdfType = item?.tipo?.nombre;
            let fileName = `${establishmentDetail?.nombre || 'Establecimiento'} - Cod SRT_${
                establishmentDetail?.codigoSRT || 'CodigoSRT'
            } -  ${pdfType}`;
            let response: any = null;
            if (pdfType === PdfType.RGRL || pdfType === PdfType.RAR) {
                handleCloseMenuAdmin();
                dispatch(
                    showRgrlDialogAlert({
                        handleWithoutConformityClick: () =>
                            handleRGRLDownload(item?.id, fileName, item, item?.tipo?.id, false),
                        handleWithConformityClick: () =>
                            handleRGRLDownload(item?.id, fileName, item, item?.tipo?.id, true),
                        isLoading: isFetchingPDFLink
                    })
                );
                return;
            } else if (pdfType === 'SVCC' || item?.tipo?.id === 5) {
                response = await triggerGetCancerigenosPdf({ idInforme: item?.id });

                const base64 = response?.data?.data;
                if (base64) {
                    downloadFromBase64(base64, item.id, fileName);
                }
                return;
            }
            if (response?.data?.data?.presignedUrls?.length >= 1) {
                response.data.data.presignedUrls.forEach((url: string, index: number) => {
                    downloadBase64File(url, item.id, fileName, index);
                });
            }
        } catch (error) {
        } finally {
            setDownloadingPDF(false);
        }
    };

    const getTranslatedPresentationType = (id: number, t: any) => {
        switch (id) {
            case 1:
                return t('types.rgrl_351');
            case 2:
                return t('types.rgrl_617');
            case 3:
                return t('types.rgrl_911');
            case 4:
                return t('types.rar');
            case 5:
                return t('types.cancerigenos');
            default:
                return '-';
        }
    };

    const handleEditClick = (tipoId: number, item: any, tipoTitulo: string) => {
        if (!item?.id) return;
        const basePath = './editar';
        const isTipoRar = tipoId === 4;
        const selectedEstablishmentId = item.establecimiento.id;
        const state = {
            selectedPresentationId: item.id,
            selectedEstablishmentId,
            tipoId,
            tipoTitulo
        };
        const path = isTipoRar ? `${basePath}-rar/${item.id}` : basePath;
        isTipoRar && dispatch(setPresentacionItemSelected(item));
        navigate(path, { state });
    };

    const handleViewClick = (tipoId: number, item: any, tipoTitulo: string) => {
        if (!item?.id) return;
        const basePath = '/backoffice/presentaciones/visualizar';
        const isTipoCancerigeno = tipoId === 5;
        const isTipoRar = tipoId === 4;
        const state = {
            selectedPresentationId: item.id,
            selectedEstablishmentId: item.establecimiento.id,
            tipoId,
            tipoTitulo
        };
        if (isTipoCancerigeno) {
            dispatch(clearSelectedReportData());
            dispatch(setSelectedCarcinogenicReportId(item.id));
        }
        const path = isTipoRar
            ? `${basePath}-rar/${item.id}`
            : isTipoCancerigeno
              ? `${basePath}-svcc/${item.id}`
              : basePath;
        isTipoRar && dispatch(setPresentacionItemSelected(item));
        navigate(path, { state });
    };

    useEffect(() => {
        if (processPdfDownload && selected?.id) {
            handleDownloadPdf(selected.tipo?.id);
            setProccessPdfDownload(false);
        }
    }, [processPdfDownload, selected, handleDownloadPdf]);

    const generateIconDynamic = (tipoId: number | undefined): React.ElementType => {
        return tipoId !== 4 ? PencilIcon : RemoveRedEyeOutlinedIcon;
    };

    return (
        <>
            {/*
             * Header más compacto, sólo en esta pantalla: título más chico y
             * "Exportar Excel" a la altura del header, como el resto del SVCC.
             * El toolbar de filtros queda afuera: usa el diseño estándar de
             * SearchToolbar, igual que en el resto de las pantallas.
             */}
            <Grid container sx={ESTILO_HEADER_COMPACTO}>
                <Grid item xs={12}>
                    <SectionHeader>
                        <SectionHeader.Title>{t('presentaciones.title')} </SectionHeader.Title>

                        {currentTabIndex === PRESENTATION_TABS_INDEX.RGRL && (
                            <Grid display="flex" flexDirection={'row'}>
                                <SectionHeader.IconHeader
                                    variant="outlined"
                                    text={'Exportar Excel'}
                                    disabled={isExportingRGRL}
                                    showIcon={false}
                                    onClick={handleExportRGRLPresentaciones}
                                />
                            </Grid>
                        )}
                        {currentTabIndex === PRESENTATION_TABS_INDEX.RAR && (
                            <Grid display="flex" flexDirection={'row'}>
                                <SectionHeader.IconHeader
                                    variant="outlined"
                                    text={'Exportar Excel'}
                                    disabled={isLoadingRARsFile}
                                    showIcon={false}
                                    onClick={
                                        currentTabIndex === PRESENTATION_TABS_INDEX.RAR
                                            ? handleDownloadRARsFile
                                            : undefined
                                    }
                                />
                            </Grid>
                        )}
                    </SectionHeader>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SearchToolbar
                        onChange={() => {}}
                        inputSearchName="nombre"
                        label={t('filters.establishmentName')}
                        onClick={handleSubmitSearch}
                        clearFilters={clearFilters}
                        showSearchInput={false}
                        showMoreFiltersButton={currentTabIndex !== PRESENTATION_TABS_INDEX.SVCC}
                        isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                        handleShowMoreFiltersPress={() =>
                            setIsShowMoreFiltersOpen((prevState: boolean) => !prevState)
                        }
                    >
                        <FiltersComponent
                            extraFilters={extraFilters}
                            handleChangeExtraFilters={handleChangeExtraFilters}
                            isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                            currentTabIndex={currentTabIndex}
                        />
                    </SearchToolbar>
                </Grid>
            </Grid>

            <Stack spacing={1} sx={{ mt: 2 }}>
                {clienteActivo === false && (
                    <Alert severity="error">El cliente seleccionado está inactivo.</Alert>
                )}
                {contratoActivo === false && (
                    <Alert severity="error">El contrato seleccionado está inactivo.</Alert>
                )}
                {organismoActivo === false && (
                    <Alert severity="error">El organismo seleccionado está inactivo.</Alert>
                )}
                {establecimientoActivo === false && (
                    <Alert severity="error">El establecimiento seleccionado está inactivo.</Alert>
                )}
            </Stack>

            {showConfirmActionAlert && !!alertType && (
                <AlertDialog
                    title={ALERT_TITLES[alertType]}
                    cancelText={t('alerts.cancel') ?? ''}
                    icon="info"
                    confirmAction={() =>
                        updatePresentationStateHandler(
                            selected?.id,
                            ALERT_CONFIRM_ACTIONS[alertType]
                        )
                    }
                    cancelAction={() => {
                        setShowConfirmActionAlert(false);
                        setAlertType(null);
                    }}
                    closeAction={() => {
                        setShowConfirmActionAlert(false);
                        setAlertType(null);
                    }}
                    loading={updatingPresentationState}
                />
            )}

            <Grid container justifyContent={'space-between'} alignItems={'center'}>
                <Grid item>
                    <Tabs value={currentTabIndex} onChange={handleTabChange} sx={TabsStyle}>
                        <Tab
                            label={t('tabs.rgrl')}
                            value={PRESENTATION_TABS_INDEX.RGRL}
                            sx={TabStyle}
                        />
                        <Tab
                            label={t('tabs.rar')}
                            value={PRESENTATION_TABS_INDEX.RAR}
                            sx={TabStyle}
                        />
                        {!isProvider && (
                            <Tab
                                label={t('tabs.svcc')}
                                value={PRESENTATION_TABS_INDEX.SVCC}
                                sx={TabStyle}
                            />
                        )}
                    </Tabs>
                </Grid>
                {AT_LEAST_ONE_PRESENTATION_SELECTED && (
                    <PresentationsBOMassiveSelectionStack
                        t={t}
                        selectedPresentations={selectedPresentations}
                        handleMassiveStateChangeClick={handleMassiveStateChangeClick}
                        handleMassiveExportClick={handleMassiveExportClick}
                        isExportingSelection={isLoadingMassiveExportByIds}
                        showMassiveExport={currentTabIndex === PRESENTATION_TABS_INDEX.RAR}
                    />
                )}
            </Grid>

            <Grid container justifyContent="center" alignItems="flex-start" spacing={3}>
                {openCard && (isMobile || isTablet) ? (
                    ''
                ) : (
                    <Grid item xs={openCard ? 6 : 12} lg={openCard ? 8 : 12}>
                        {/*
                         * La tab SVCC usa la grilla por CUIT: la presentación
                         * ante la SRT es por CUIT, no por informe, así que la
                         * tabla por informe no representaba la unidad de
                         * trabajo (un error en cualquier establecimiento frena
                         * el lote entero). Trae sus propios datos, filtros y
                         * acciones por fila.
                         */}
                        {currentTabIndex === PRESENTATION_TABS_INDEX.SVCC && !isProvider ? (
                            <PresentacionesSVCCIndex
                                embebido
                                cuitContrato={
                                    rawContracts.find(
                                        (contrato: any) =>
                                            contrato.id === extraFilters?.idOrganismoCentral
                                    )?.cuit
                                }
                                idOrganizacion={extraFilters?.idOrganizacion}
                            />
                        ) : itemTable?.data?.data.length === undefined && !isFetching ? (
                            <Box
                                my={1}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexDirection="column"
                            >
                                <Stack
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{
                                        '& h4': {
                                            fontSize: '18px'
                                        },
                                        '& p': {
                                            maxWidth: '360px',
                                            textAlign: 'center',
                                            margin: 0,
                                            fontSize: '16px'
                                        }
                                    }}
                                >
                                    <img
                                        src={isAdminBO ? NoPresentationsBO : NoPresentationsSIMP}
                                        alt={t('noRecords.imageAlt') ?? ''}
                                    />
                                    <h4>{t('noRecords.title')}</h4>
                                    <p>{t('noRecords.message')}</p>
                                </Stack>
                            </Box>
                        ) : (
                            /*
                             * Misma tarjeta que la grilla SVCC: borde redondeado,
                             * header claro y la paginación adentro.
                             */
                            <Box
                                sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', p: 2 }}
                                data-testid="tabla-presentaciones"
                            >
                                <Box sx={ESTILO_TABLA_PRESENTACIONES}>
                                    <DataTable
                                        columnTitles={[
                                            ...(currentTabIndex !== PRESENTATION_TABS_INDEX.SVCC
                                                ? [
                                                      {
                                                          label: '',
                                                          showSelectAllCheck: true,
                                                          checked: areAllItemsSelected(
                                                              selectablePresentations(
                                                                  itemTable?.data?.data ?? []
                                                              )
                                                          ),
                                                          onSelectAllCheckPress: () =>
                                                              handleSelectAllClick(
                                                                  selectablePresentations(
                                                                      itemTable?.data?.data ?? []
                                                                  )
                                                              )
                                                      }
                                                  ]
                                                : []),
                                            ...columnTitles({
                                                t,
                                                currentTabIndex,
                                                hideSrtState: isViewerBO
                                            })
                                        ]}
                                        columnWidths={columnWidths(currentTabIndex, isViewerBO)}
                                        onSelectRow={() => {}}
                                        isLoading={isFetching}
                                        items={itemTable}
                                        filters={filters}
                                        setFilters={setFilters}
                                        noPaginated={false}
                                        selected={selected}
                                        cellClassName={styles}
                                        headers={[
                                            ...(currentTabIndex !== PRESENTATION_TABS_INDEX.SVCC
                                                ? [
                                                      {
                                                          type: 'data',
                                                          centerColumn: true,
                                                          upperLabel: (item: any) => (
                                                              <Checkbox
                                                                  checked={isSelected(item)}
                                                                  disabled={
                                                                      !isPresentationSelectable(
                                                                          item
                                                                      )
                                                                  }
                                                                  onClick={(e) =>
                                                                      e.stopPropagation()
                                                                  }
                                                                  onChange={(e) => {
                                                                      e.stopPropagation();
                                                                      handleSelectClick(item);
                                                                  }}
                                                              />
                                                          )
                                                      }
                                                  ]
                                                : []),
                                            {
                                                type: 'data',
                                                upperLabel: (item: any) => item?.id ?? '-',
                                                centerColumn: true
                                            },
                                            ...(currentTabIndex === PRESENTATION_TABS_INDEX.RGRL
                                                ? [
                                                      {
                                                          type: 'data',
                                                          upperLabel: (item: any) => {
                                                              const value = item?.tieneCancerigenos;
                                                              if (
                                                                  value === null ||
                                                                  value === undefined
                                                              )
                                                                  return '-';
                                                              return value ? t('SI') : t('NO');
                                                          }
                                                      }
                                                  ]
                                                : []),
                                            ...(currentTabIndex === PRESENTATION_TABS_INDEX.RAR
                                                ? [
                                                      {
                                                          type: 'data',
                                                          upperLabel: (item: any) => {
                                                              const value = item?.tieneExpuestos;
                                                              if (
                                                                  value === null ||
                                                                  value === undefined
                                                              )
                                                                  return '-';
                                                              return value ? t('SI') : t('NO');
                                                          }
                                                      }
                                                  ]
                                                : []),
                                            {
                                                type: 'data',
                                                upperLabel: (item: any) =>
                                                    item?.contrato
                                                        ? `${item.contrato.cuitOrganismoCentral} - ${item.contrato.razonSocial}`
                                                        : '-',
                                                align: 'left'
                                            },
                                            {
                                                type: 'data',
                                                upperLabel: (item: any) =>
                                                    item?.establecimiento
                                                        ? `${item.establecimiento.nombre} (Código SRT: ${item.establecimiento.codigoSRT})`
                                                        : '-'
                                            },
                                            {
                                                type: 'data',
                                                centerColumn: true,
                                                upperLabel: (item: any) =>
                                                    item?.fechaPresentacion && (
                                                        <Tooltip
                                                            title={tooltipPresentationDateContent({
                                                                presentation: item
                                                            })}
                                                            enterDelay={200}
                                                        >
                                                            <span>
                                                                {getDate(item?.fechaPresentacion) ||
                                                                    '-'}
                                                            </span>
                                                        </Tooltip>
                                                    )
                                            },
                                            {
                                                type: 'data',
                                                centerColumn: true,
                                                upperLabel: (item: any) =>
                                                    item?.idEstado ? (
                                                        <PresentationStateChip
                                                            presentationState={item?.idEstado}
                                                        />
                                                    ) : (
                                                        '-'
                                                    )
                                            },
                                            currentTabIndex == PRESENTATION_TABS_INDEX.SVCC &&
                                                !isViewerBO && {
                                                    type: 'data',
                                                    centerColumn: true,
                                                    upperLabel: (item: any) => (
                                                        <PresentationSRTStateChip
                                                            presentationState={getPresentationSVCCStateSRT(
                                                                item?.cancerigenosSrt
                                                            )}
                                                        />
                                                    )
                                                },
                                            {
                                                type: 'presentation-options',
                                                onClick: (e: any, item: any) => {
                                                    setAnchorEl(e.currentTarget);
                                                    setSelected((prevState) => item);
                                                },
                                                upperLabel: (item: any, e: any) => (
                                                    <Box
                                                        display={'flex'}
                                                        gap={1}
                                                        justifyContent={'right'}
                                                        pr={1}
                                                    >
                                                        {FEATURES.GESTION_PRESENTACIONES
                                                            .hasAccess && (
                                                            <TableActionButton
                                                                displayOption={
                                                                    (isOperatorSIMP ||
                                                                        isSupervisorSIMP) &&
                                                                    item?.idEstado !==
                                                                        PRESENTATION_STATE.BORRADOR &&
                                                                    item?.idEstado !==
                                                                        PRESENTATION_STATE.PROCESANDO &&
                                                                    item?.idEstado !==
                                                                        PRESENTATION_STATE.ERROR_PROCESAMIENTO &&
                                                                    item?.idEstado !==
                                                                        !(
                                                                            item?.tipo?.id === 4 &&
                                                                            item?.idEstado ===
                                                                                PRESENTATION_STATE.RECIBIDO
                                                                        )
                                                                }
                                                                onClick={() => {
                                                                    if (
                                                                        item?.idEstado ===
                                                                        PRESENTATION_STATE.CANCELADO
                                                                    ) {
                                                                        return;
                                                                    }
                                                                    handleDownloadPresentation(
                                                                        item
                                                                    );
                                                                }}
                                                                disabled={
                                                                    item?.idEstado ===
                                                                        PRESENTATION_STATE.CANCELADO ||
                                                                    downloadingPDF
                                                                }
                                                                IconComponent={
                                                                    PictureAsPdfOutlinedIcon
                                                                }
                                                            />
                                                        )}

                                                        {FEATURES.CAMBIAR_ESTADO_PRESENTACION
                                                            .hasAccess && (
                                                            <TableActionButton
                                                                displayOption={
                                                                    isAdminBO &&
                                                                    item?.tipo?.id !== 5
                                                                }
                                                                onClick={(event: any) => {
                                                                    if (
                                                                        item?.idEstado ===
                                                                        PRESENTATION_STATE.CANCELADO
                                                                    ) {
                                                                        return;
                                                                    }
                                                                    handleClickAction(item, event);
                                                                }}
                                                                disabled={
                                                                    item?.idEstado ===
                                                                        PRESENTATION_STATE.CANCELADO ||
                                                                    isContratoInactivo(item)
                                                                }
                                                                IconComponent={ToggleStateIcon}
                                                            />
                                                        )}

                                                        {FEATURES.ALTA_PRESENTACION.hasAccess && (
                                                            <TableActionButton
                                                                displayOption={
                                                                    (isOperatorSIMP ||
                                                                        isSupervisorSIMP) &&
                                                                    (item?.idEstado ===
                                                                        PRESENTATION_STATE.BORRADOR ||
                                                                        item?.idEstado ===
                                                                            PRESENTATION_STATE.ERROR_PROCESAMIENTO ||
                                                                        item?.idEstado ===
                                                                            PRESENTATION_STATE.PROCESANDO)
                                                                }
                                                                disabled={
                                                                    item?.idEstado ===
                                                                    PRESENTATION_STATE.CANCELADO
                                                                }
                                                                onClick={() => {
                                                                    if (
                                                                        item?.idEstado ===
                                                                        PRESENTATION_STATE.CANCELADO
                                                                    ) {
                                                                        return;
                                                                    }
                                                                    setSelected(item);
                                                                    if (isContratoInactivo(item)) {
                                                                        handleViewClick(
                                                                            item.tipo?.id,
                                                                            item,
                                                                            item.tipo?.titulo
                                                                        );
                                                                        return;
                                                                    }
                                                                    handleEditClick(
                                                                        item.tipo?.id,
                                                                        item,
                                                                        item.tipo?.titulo
                                                                    );
                                                                }}
                                                                IconComponent={
                                                                    isContratoInactivo(item)
                                                                        ? RemoveRedEyeOutlinedIcon
                                                                        : generateIconDynamic(
                                                                              item.tipo?.id
                                                                          )
                                                                }
                                                            />
                                                        )}

                                                        <TableActionButton
                                                            displayOption={
                                                                isAdminBO || isViewerBO || isProvider
                                                            }
                                                            onClick={(event: any) => {
                                                                setAdminSelectedItem(item);
                                                                handleClickActionAdmin(item, event);
                                                            }}
                                                            IconComponent={ExpandMoreIcon}
                                                        />
                                                    </Box>
                                                )
                                            }
                                        ].filter(Boolean)}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Grid>
                )}
                {openCard && (
                    <Grid
                        item
                        xs={!isMobile && !isTablet ? 6 : 12}
                        lg={!isMobile && !isTablet ? 4 : 12}
                    ></Grid>
                )}
            </Grid>
            <Menu id="basic-menu" anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
                <Box>
                    <MenuItem
                        disabled={
                            selected?.estado === PRESENTATION_STATE.EN_REVISION ||
                            selected?.idEstado === PRESENTATION_STATE.EN_REVISION
                        }
                        onClick={() => {
                            setAlertType(ALERT_TYPES.CONFIRM_REVISE as ALERT_TYPE);
                            handleCloseMenu();
                        }}
                    >
                        {t('states.en_revision')}
                    </MenuItem>
                    <MenuItem
                        disabled={
                            selected?.estado === PRESENTATION_STATE.PENDIENTE_VISITA ||
                            selected?.idEstado === PRESENTATION_STATE.PENDIENTE_VISITA
                        }
                        onClick={() => {
                            setAlertType(ALERT_TYPES.CONFIRM_VISIT as ALERT_TYPE);
                            handleCloseMenu();
                        }}
                    >
                        {t('states.pendiente_visita')}
                    </MenuItem>
                    <MenuItem
                        disabled={
                            selected?.estado === PRESENTATION_STATE.VISADO ||
                            selected?.idEstado === PRESENTATION_STATE.VISADO
                        }
                        onClick={() => {
                            setAlertType(ALERT_TYPES.CONFIRM_SEEN as ALERT_TYPE);
                            handleCloseMenu();
                        }}
                    >
                        {t('states.visado')}
                    </MenuItem>
                    <MenuItem
                        disabled={
                            selected?.estado === PRESENTATION_STATE.PRESENTADO ||
                            selected?.idEstado === PRESENTATION_STATE.PRESENTADO
                        }
                        onClick={() => {
                            setAlertType(ALERT_TYPES.CONFIRM_CONFORM as ALERT_TYPE);
                            handleCloseMenu();
                        }}
                    >
                        {t('states.presentado')}
                    </MenuItem>
                </Box>
            </Menu>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={menuOpenAdmin}
                onClose={handleCloseMenuAdmin}
            >
                <Box px={0.5}>
                    {/* REMOVED */}
                    {/* {adminSelectedItem?.tipo?.id === 5 &&
                        adminSelectedItem?.idEstado === PRESENTATION_STATE.RECIBIDO && (
                            <MenuItem onClick={() => handleApproveCancerigeno(adminSelectedItem)}>
                                {t('actions.approve')}
                            </MenuItem>
                        )} */}

                    {FEATURES.VISUALIZACION_DE_DETALLE_PRESENTACION.hasAccess && (
                        <MenuItem
                            disabled={adminSelectedItem?.idEstado === PRESENTATION_STATE.CANCELADO}
                            onClick={() => {
                                if (adminSelectedItem?.idEstado === PRESENTATION_STATE.CANCELADO) {
                                    return;
                                }
                                handleViewClick(
                                    adminSelectedItem?.tipo?.id,
                                    adminSelectedItem,
                                    adminSelectedItem?.tipo?.titulo
                                );
                                handleCloseMenu();
                            }}
                        >
                            <>
                                <RemoveRedEyeOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                                <Typography>{t('actions.view')}</Typography>
                            </>
                        </MenuItem>
                    )}

                    <MenuItem
                        disabled={
                            adminSelectedItem?.idEstado === PRESENTATION_STATE.CANCELADO ||
                            downloadingPDF
                        }
                        onClick={() => {
                            if (adminSelectedItem?.idEstado === PRESENTATION_STATE.CANCELADO) {
                                return;
                            }
                            handleDownloadPresentation(adminSelectedItem);
                        }}
                    >
                        <>
                            <FileDownloadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography>{t('actions.download')}</Typography>
                        </>
                    </MenuItem>

                    {currentTabIndex === PRESENTATION_TABS_INDEX.SVCC && !isViewerBO && !isProvider && (
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                if (selected) {
                                    presentationsDrawerManager.setOpen(
                                        PRESENTATIONS_DRAWER_ACTION_VALUES.SRT_INFORMATION
                                    );
                                    handleCloseMenuAdmin();
                                }
                            }}
                            sx={{ color: '#1269d1' }}
                        >
                            <>
                                <DescriptionOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                                <Typography>{t('actions.srtInformation')}</Typography>
                            </>
                        </MenuItem>
                    )}
                    {FEATURES.ELIMINACION_PRESENTACION.hasAccess &&
                        adminSelectedItem?.tipo?.id !== 5 && (
                            <MenuItem
                                disabled={
                                    adminSelectedItem?.idEstado === PRESENTATION_STATE.CANCELADO ||
                                    isContratoInactivo(adminSelectedItem)
                                }
                                onClick={() => {
                                    if (
                                        adminSelectedItem?.idEstado ===
                                            PRESENTATION_STATE.CANCELADO ||
                                        isContratoInactivo(adminSelectedItem)
                                    ) {
                                        return;
                                    }
                                    handleCloseMenu();
                                    handleCloseMenuAdmin();
                                    if (selected) {
                                        setAlertType(ALERT_TYPES.CONFIRM_CANCEL as ALERT_TYPE);
                                    }
                                }}
                                sx={{ color: '#dc2626' }}
                            >
                                <>
                                    <DeleteOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                                    <Typography>{t('actions.cancel')}</Typography>
                                </>
                            </MenuItem>
                        )}
                </Box>
            </Menu>
            <DrawerManager
                open={presentationsDrawerManager.open}
                setOpen={presentationsDrawerManager.setOpen}
                closeDrawer={presentationsDrawerManager.closeDrawer}
                selected={selected}
                handleSendCarcinogenicReportToSRT={handleSendCarcinogenicReportToSRT}
                isSendingCarcinogenicReportToSRT={isSendingCarcinogenicReportToSRT}
                translate={t}
            />
            <BackdropComponent
                loading={
                    edittingPresentationState ||
                    isLoading ||
                    isLoadingRARsFile ||
                    downloadingPDF ||
                    isFetchingPDFLink ||
                    isUpdatingMassivePresentationsStates ||
                    isLoadingMassiveExportByIds ||
                    isSendingCarcinogenicReportToSRT
                }
                text={
                    isLoadingRARsFile || isLoadingMassiveExportByIds
                        ? 'Generando archivo, por favor espere...'
                        : undefined
                }
            />
        </>
    );
};

export default PresentacionesModuleIndex;
